/* array_grid.control_adapter.js */

"use strict";
/* globals Slick, ResizeObserver*/
const $ = require("jquery");
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const q = require("q");
const GridSelectionModel = require("../GridSelectionModel");
const GridComboBoxEditor = require("./grid_editors_formatters/GridComboboxEditor");
const GridRefComboBoxEditor = require("./grid_editors_formatters/GridRefComboboxEditor");
const GridSelectComboBoxEditor = require("./grid_editors_formatters/GridSelectComboboxEditor");
const GridCheckBoxEditor = require("./grid_editors_formatters/GridCheckboxEditor");
const GridNumberEditor = require("./grid_editors_formatters/GridNumberEditor");
const GridTextEditor = require("./grid_editors_formatters/GridTextEditor");
const ValueBuilder = require("../ValueBuilder");
const subColumnFormatter = require("./grid_editors_formatters/SubColumnFormatter");
const GlueJay = require("@gluenetworks/gluejay").Gluejay;
const launchRef = require("../launchRef");
const StyleUtil = require("../StyleUtil");

const rowNumField = "@rowNum";
const idField = "@id";
const minimumRowSize = 2;
const defaultMinLineCount = 15;

/**
 * @event ArrayGrid#ValueChanged
 * @type {Object}
 * @property oldValue {string} The previous value
 * @property newValue {string} The new value
 */

/**
 * @class ArrayGridAdapter
 * @classdesc renders the Array Table Jtype.
 *
 * @param logger {Bunyan} The logger
 * @param containingElem {DomElement} The containing dom element for this control
 * @param formCtrlDef {FormControlDefinition} The definition of this control object.
 *
 * @requires util
 * @requires EventEmitter
 *
 * @fires ArrayGridAdapter#ValueChanged
 */

class ArrayGridAdapter extends EventEmitter {
    /*
     * Tell the Form renderer that this is an Angular Control.
     * @returns {boolean}
     */
    static get isAngularControl() {
        return true;
    }

    /**
     * Tell the Form renderer that this adapter supports multi edit mode.
     * @returns {boolean}
     */
    static get isMultiEditSupported() {
        return true;
    }

    constructor(logger, containingElem, formCtrlDef) {
        super();
        this._multiEditMode = _.get(formCtrlDef, "displayModeOptions.multiEditMode", false);
        this.loadedDeferred = q.defer();
        this.addedItemPromises = [];
        this._oldValue = {};
        this._oldInProgressStatus = false;
        this.name = formCtrlDef.name;
        this.templateName = "templatecache/templates/array_grid.template.html";
        this.arrayParentElem = {};
        this.fcd = formCtrlDef;
        this.containingElem = containingElem;
        this.logger = logger;
        this.$init.$inject = ["$scope", "$templateCache", "$timeout", "$compile"];
        this.$compiledAndLinked.$inject = ["$scope", "$timeout"];
        this.gridId = 0;
        this._dataModel = [];
        this.rowHeight = 25; // This is just an estimate.  We will set the real value when a row is added.
        this._prevRatio = undefined;
    }

    static rowFormatter(row) {
        return row + 1;
    }

    // ******************************************
    // Lifecycle functions
    // ******************************************
    _registerEvents() {
        this.dataView.onRowCountChanged.subscribe(
            (this._onRowCountChange = this._onRowCountChange.bind(this))
        );
        this.dataView.onRowsChanged.subscribe((this._onRowChange = this._onRowChange.bind(this)));
        this.grid.onCellChange.subscribe((this._onCellChange = this._onCellChange.bind(this)));
        this.grid.onScroll.subscribe((this._onScroll = this._onScroll.bind(this)));
        this.grid.onSelectedRowsChanged.subscribe(
            (this._onSelectedRowsChanged = this._onSelectedRowsChanged.bind(this))
        );
        this.grid.onClick.subscribe((this._onClick = this._onClick.bind(this)));
        this.grid.onDblClick.subscribe((this._onDblClick = this._onDblClick.bind(this)));
        this.grid.onSort.subscribe((this._onSort = this._onSort.bind(this)));
        this.grid.onBeforeEditCell.subscribe(
            (this._onBeforeEditCell = this._onBeforeEditCell.bind(this))
        );
        if (_.isFunction(ResizeObserver)) {
            this.gridResizeObserver = new ResizeObserver(
                (this._onGridResize = this._onGridResize.bind(this))
            );
            this.gridResizeObserver.observe(this.gridContainer[0]);
        }
        document.addEventListener("click", (this._onDocClick = this._onDocClick.bind(this)));
        if (this._multiEditApply) {
            this._multiEditApply.addEventListener(
                "click",
                (this._onMultiApply = this._onMultiApply.bind(this))
            );
        }
        let options = {
            root: null,
            rootMargin: "0px",
            threshold: [0.1],
        };

        this._observer = new IntersectionObserver(
            (this._handleIntersect = this._handleIntersect.bind(this)),
            options
        );
        this._observer.observe(this.containingElem[0]);
        this.listenersInitialized = true;
    }

    _unregisterEvents() {
        this.dataView.onRowCountChanged.unsubscribe(this._onRowCountChange);
        this.dataView.onRowsChanged.unsubscribe(this._onRowChange);
        this.grid.onCellChange.unsubscribe(this._onCellChange);
        this.grid.onScroll.unsubscribe(this._onScroll);
        this.grid.onSelectedRowsChanged.unsubscribe(this._onSelectedRowsChanged);
        this.grid.onClick.unsubscribe(this._onClick);
        this.grid.onDblClick.unsubscribe(this._onDblClick);
        this.grid.onSort.unsubscribe(this._onSort);
        this.grid.onBeforeEditCell.unsubscribe(this._onBeforeEditCell);
        if (this.gridResizeObserver) {
            this.gridResizeObserver.unobserve(this.gridContainer[0]);
        }
        document.removeEventListener("click", this._onDocClick);
        this.detailFormApi.removeListener("ValueChanged", this._onDetailFormChange);
        this.detailFormApi.removeListener("InProgress", this._onDetailFormInProgress);
        this.detailFormApi.removeListener("MultiEditActive", this._onDetailFormMultiEditActive);
        if (this._multiEditApply) {
            this._multiEditApply.removeEventListener("click", this._onMultiApply);
        }
        this._observer.disconnect();
    }

    // **************************************************************
    // External API
    // **************************************************************

    getValue() {
        return this._dataModel;
    }

    setValue(value) {
        this._loadData(value);
        return this.getValue();
    }

    isInProgress() {
        return this.scope.detailFormVisible && this.detailFormApi.isInProgress();
    }

    tearDown() {
        if (this.listenersInitialized) {
            this._unregisterEvents();
            if (this.grid.getActiveCell()) {
                this.grid.setActiveCell(0, 0);
            }
            this.detailFormApi.tearDown();
        }
    }

    getMultiEditActive() {
        return this._multiEditActive;
    }

    setMultiEditActive(flag) {
        if (!this._readOnlyField) {
            this.setReadOnly(!flag);
            let oldValue = this._multiEditActive;
            this._multiEditActive = flag;
            if (oldValue !== this._multiEditActive) {
                this.emit("MultiEditActive", {
                    oldValue: oldValue,
                    newValue: this._multiEditActive,
                });
            }
        }
    }

    /**
     * Set the components display based on read only state.
     * @param {boolean} flag - The current state of the read only
     */
    setReadOnly(flag) {
        this._readOnlyOn = flag;
        // If read only being set for multi-edit then turn off all fields
        if (flag) {
            this.allowDetailForm = false;
            this.scope.showAdd = false;
            this.scope.showDelete = false;
            this.scope.showReorder = false;
            this._hideDetailForm();
            this.gridContainer.find(".grid-canvas").addClass("read-only");
        } else {
            this.allowDetailForm = _.get(
                this.fcd,
                "configSettings.Display.table.showDetailForm",
                true
            );
            this.scope.showAdd = _.get(this.fcd, "configSettings.Display.table.showAdd", true);
            this.scope.showDelete = _.get(
                this.fcd,
                "configSettings.Display.table.showDelete",
                true
            );
            this.scope.showReorder = _.get(
                this.fcd,
                "configSettings.Display.table.showReorder",
                true
            );
            this.gridContainer.find(".grid-canvas").removeClass("read-only");
        }
        this.scope.showDetailFormIcon = this.allowDetailForm;
        setTimeout(() => {
            this.scope.$apply();
        });
    }

    /**
     * A Hook to start the angular work, due to timing issues don't try anything angularis before this hook is called.
     * @param $scope {$scope} Angular $scope
     * @param $templateCache {$templateCache}
     * @param $scope - The scope related to angular
     * @param $templateCache - The template cache
     */
    $init($scope, $templateCache /*, $timeout, $compile*/) {
        let template = $templateCache.get("templatecache/templates/array_grid.template.html");
        let grid = $(template);
        this.containingElem.append(grid);
        this._fireComplete();

        this.scope = $scope;
        $scope.disableMove = true;
        $scope.fcd = this.fcd;

        $scope.formCtrlDef = {
            displayInfo: {
                label: _.get(this.fcd, "Display.label", this.name),
                title: _.get(this.fcd, "Display.tooltip", this.name),
            },
        };

        $scope.addRow = () => {
            this.addRow();
        };

        $scope.deleteRows = () => {
            this.deleteRows();
        };

        $scope.movePressed = (up) => {
            this._moveRowPressed(up);
        };

        $scope.moveReleased = () => {
            this._moveRowReleased();
        };

        $scope.launchRef = (event) => {
            this._launchRef(event);
        };

        $scope.showDetailForm = (/*event*/) => {
            this._showDetailForm();
        };

        $scope.hideDetailForm = (/*event*/) => {
            this._hideDetailForm();
        };

        $scope.toggleDetailForm = (/*event*/) => {
            if (this.scope.showDetailDropDown()) {
                if (this.scope.detailFormVisible) {
                    this._hideDetailForm();
                } else {
                    this._showDetailForm();
                }
            }
        };

        $scope.showDetailDropDown = () => {
            return this._showDetailFormDropDown();
        };

        this.allowDetailForm = _.get(this.fcd, "configSettings.Display.table.showDetailForm", true);
        this._multiEdit = _.get(this.fcd, "configSettings.Display.table.multiEdit", false);
        this.scope.showAdd = _.get(this.fcd, "configSettings.Display.table.showAdd", true);
        this.scope.showReorder = _.get(this.fcd, "configSettings.Display.table.showReorder", true);
        this.scope.showDelete = _.get(this.fcd, "configSettings.Display.table.showDelete", true);
        this.scope.showDetailFormIcon = this.allowDetailForm;

        this._setupDetailForm($scope);

        let stylesConfig = _.get(this.fcd, "configSettings.Display.styles", {});
        StyleUtil.renderStylesConfig(stylesConfig, this.containingElem[0]);

        // Apply item layout styles - these styles customize how this element is placed inside parent element
        let itemLayoutCssClass = _.get($scope, "jTypeContext.layout.item.cssClass", "");
        this.containingElem.addClass(itemLayoutCssClass);

        return this.loadedDeferred.promise;
    }

    /**
     * A Hook for when this control has been compiled/Linked.
     * @param $scope {$scope} The Angular Scope variable
     * @param $timeout {$timeout} The Angular $timeout variable
     *
     * if $compiledAndLinked.$inject is specified those injection parameters will be used as the arguments
     */
    $compiledAndLinked($scope, $timeout) {
        this._initializeGrid();
        if (_.get(this.formCtrlDef, "configSettings.Display.readOnly", false)) {
            this._readOnlyField = true;
        }
        let currentData = [];
        if (this.fcd.configData && !_.isEmpty(this.fcd.configData)) {
            currentData = this.fcd.configData;
        }

        this._loadData(currentData);
        this._resizeGrid();
        if (this.fcd.configData && !_.isEmpty(this.fcd.configData)) {
            //Update used/available based on configData
            $timeout(() => {
                this._setInitialReadOnlyState();
                this.loadedDeferred.resolve();
            });
        } else {
            this._setInitialReadOnlyState();
            this.loadedDeferred.resolve();
        }

        // If multiedit is allowed, add in the apply button
        if (this._multiEdit) {
            // Find the detail form.
            this._multiEditApply = document.createElement("button");
            this._multiEditApply.classList.add("multi_edit_apply");
            this._multiEditApply.innerHTML = "Apply To Selected Rows";
        }

        this._registerEvents();
    }

    // ******************************************************
    // Angular invoked functions
    // ******************************************************

    addRow() {
        if (this.grid.getEditorLock().isActive() && !this.keepEditorOpen) {
            this.grid.getEditController().commitCurrentEdit();
        }
        let position = 0;
        let newRecord = this._getDefaultRow();
        let selectedRows = this.grid.getSelectedRows();
        if (_.isEmpty(selectedRows)) {
            newRecord[rowNumField] = this.dataView.getLength() + 1;
            this.dataView.addItem(newRecord);
            this.dataView.refresh();
            position = this.grid.getDataLength() - 1;
        } else {
            let lastSelectedRow = 0;
            selectedRows.forEach((selectedRowIdx) => {
                if (lastSelectedRow < selectedRowIdx) {
                    lastSelectedRow = selectedRowIdx;
                }
            });
            position = lastSelectedRow + 1;
            newRecord[rowNumField] = position + 1;
            this.dataView.insertItem(position, newRecord);
            this._resetRowNumbers();
        }
        let newSelectedRow = [];
        newSelectedRow.push(position);
        this.keepEditorOpen = true;
        setTimeout(() => {
            this.keepEditorOpen = false;
        }, 100);
        this.grid.setSelectedRows(newSelectedRow);
        if (position + 1 < this.dataView.getLength()) {
            this.grid.scrollRowIntoView(position + 1);
        } else {
            this.grid.scrollRowIntoView(position);
        }
        this._cancelSort();
        this.grid.setActiveCell(position, this.firstEditableCol);
        this.grid.editActiveCell();
    }

    deleteRows() {
        let selectedItems = [];
        let selectedIndexes = _.sortBy(this.grid.getSelectedRows());
        this.dataView.beginUpdate();
        selectedIndexes.forEach((idx) => {
            selectedItems.push(this.dataView.getItem(idx));
        });

        let itemsDeleted = false;
        selectedItems.forEach((item) => {
            this.dataView.deleteItem(item[idField]);
            itemsDeleted = true;
        });
        this.dataView.endUpdate();

        if (!_.isEmpty(selectedIndexes)) {
            this._resetRowNumbers();
        }

        if (itemsDeleted) {
            this.grid.invalidate();
            this.grid.render();
            this.grid.setSelectedRows([]);
        }
    }

    _moveRowPressed(up) {
        this._moveRows(up);
        this.movePressed = true;
        this.repeatMoves = 0;

        this.moveTimer = setTimeout(() => {
            this._moveRows(up);
            this._checkMovePressed(up);
        }, 1000);
    }

    _moveRowReleased() {
        this.movePressed = false;
        if (this.rapidMove) {
            this.rapidMove = false;
            this._fireValueChanged();
        }

        if (this.moveTimer) {
            clearTimeout(this.moveTimer);
            this.moveTimer = null;
        }
    }

    // ***********************************************
    // Utility Functions
    // ***********************************************

    /**
     * Get the default values of the row insertion.
     *
     * @private
     */
    _getDefaultRow() {
        let defaultRow = _.cloneDeep(this._defaultRow);
        this.gridId++;
        defaultRow[idField] = this.gridId;
        return defaultRow;
    }

    /**
     * Load the initial data for the grid.
     * @private
     */
    _loadData(data) {
        this._dataModel = data;
        let gridData = _.cloneDeep(data);
        let refFields = [];
        this.columns.forEach((column, index) => {
            let fcdColumnDef = _.get(column, "fcdColumnDef");
            if (fcdColumnDef) {
                if (fcdColumnDef.fieldType === "Ref") {
                    refFields.push({field: column.field, colIdx: index});
                }
            }
        });
        gridData.forEach((row, rowIdx) => {
            // Add in the "id" field needed to insert data into slickgrid.
            this.gridId++;
            row[idField] = this.gridId;
            row[rowNumField] = "row";
            // This is a little bit complicated but what is going on here is that
            // the ref values are being swapped out for a more familiar display in json.
            // The control backend strips out the instanceName and other fields when the data
            // is store, so when retrieving it back, certain fields are missing that the control
            // UI user is used to seeing when they look at the json directly.  This is just restoring
            // the data to what the control user is used to seeing.
            refFields.forEach((refField) => {
                let refValue = this._dataModel[rowIdx][refField.field];
                if (!_.isEmpty(refValue)) {
                    let instanceId = _.get(refValue, "@ref.instance.instanceId");
                    refValue = this.fcdColumnDefs[refField.colIdx].instanceMap[instanceId];
                    // Replace it in both locations.
                    this._dataModel[rowIdx][refField.field] = _.cloneDeep(refValue);
                    row[refField.field] = _.cloneDeep(refValue);
                }
            });
        });
        this.dataView.setItems(gridData, idField);
    }

    _setInitialReadOnlyState() {
        if (this._readOnlyField) {
            this.setReadOnly(true);
        }
        if (this._multiEditMode) {
            this.setReadOnly(true);
        }
    }

    /**
     * Initialize the columns for the grid based on the jtype, then initialize
     * the base grid.
     * @private
     */
    _initializeGrid() {
        this.dataView = new Slick.Data.DataView();
        this.columns = [];
        this.fcdColumnDefs = [];
        this.firstEditableCol = 0;
        let addHiddenDataCol = false;
        let showLineNumbers = _.get(this.fcd, "configSettings.Display.table.showLineNumbers", true);
        if (showLineNumbers) {
            let rowNumCol = {
                id: rowNumField,
                name: "#",
                field: rowNumField,
                minWidth: 50,
                width: 50,
                maxWidth: 80,
                formatter: ArrayGridAdapter.rowFormatter,
            };

            this.columns.push(rowNumCol);
            this.firstEditableCol = 1;
            this.fcdColumnDefs.push({dummyRowCountCol: true});
        }
        this._defaultRow = {};
        let propOrder = _.get(this.fcd, "configSettings.value[0].value.@propertyOrder");
        let colConfigSettings = _.get(this.fcd, "configSettings.value[0].value");
        let columnDisplays = _.get(this.fcd, "configSettings.Display.table.columns");
        let deepColumns = {};

        // Parse out any deep properties that will be displayed as columns in the table.
        if (columnDisplays) {
            let columnKeys = Object.keys(columnDisplays);
            columnKeys.forEach((key) => {
                let firstSegIdx = key.indexOf(".");
                let primePropName;
                if (firstSegIdx > 0) {
                    let objectSegments = key.split(".");
                    primePropName = objectSegments[0];
                    if (!deepColumns[primePropName]) {
                        deepColumns[primePropName] = [];
                    }
                    let columnDisplay = columnDisplays[key];
                    columnDisplay["@displayValue"] = key.substring(firstSegIdx + 1);
                    deepColumns[primePropName].push(columnDisplay);
                }
            });
        }

        // For each property in the object attempt to add a column for it.
        propOrder.forEach((columnDefName) => {
            let allowEdit = _.get(this.fcd, "configSettings.Display.table.allowEdit", true);
            let addColumn = true;
            let fcdColumnDef = colConfigSettings[columnDefName];
            // Initialize the display info
            fcdColumnDef.displayinfo = {};
            fcdColumnDef.displayinfo.label = _.get(fcdColumnDef, "Display.label", columnDefName);
            fcdColumnDef.displayinfo.tooltip = _.get(
                fcdColumnDef,
                "Display.description",
                fcdColumnDef.displayinfo.label
            );
            fcdColumnDef.displayinfo.placeholder = _.get(
                fcdColumnDef,
                "Display.placeholder",
                fcdColumnDef.displayinfo.label
            );
            allowEdit = !_.get(
                this.fcd,
                "configSettings.Display.table.columns." + columnDefName + ".readOnly",
                !allowEdit
            );
            allowEdit = !_.get(fcdColumnDef, "Display.readOnly", !allowEdit);
            this.fcdColumnDefs.push(fcdColumnDef);
            let columnName = _.get(fcdColumnDef, "Display.label", columnDefName);
            columnName = _.get(
                this.fcd,
                "configSettings.Display.table.columns." + columnDefName + ".label",
                columnName
            );
            let columnHeading =
                '<div class="column_header"><div class="column_name">' + columnName + "</div>";
            if (allowEdit) {
                columnHeading +=
                    '<base-icon-gl data-icon="form.edit" data-dark-theme="true" ></base-icon-gl>';
            }
            columnHeading += "</div>";
            let editor = null;
            let comboBoxOptions = [];
            let formatter = null;
            let refColumn = false;
            // If the data is hidden in any way then don't add the column
            if (
                _.get(fcdColumnDef, "Display.hidden", false) ||
                _.get(
                    this.fcd,
                    "configSettings.Display.table.columns." + columnDefName + ".hidden",
                    false
                )
            ) {
                addColumn = false;
                // If the data is only hidden in the column then we should show the additional data column.
                if (
                    _.get(
                        this.fcd,
                        "configSettings.Display.table.columns." + columnDefName + ".hidden",
                        false
                    )
                ) {
                    addHiddenDataCol = true;
                }
            } else {
                let fieldType = fcdColumnDef.fieldType;
                // This is a bit of a hack because multi refs are not handled well in a table
                if (_.get(fcdColumnDef, "Ref.many", false)) {
                    fieldType = "MultiRef";
                }
                switch (fieldType) {
                    case "String": {
                        editor = GridTextEditor;
                        break;
                    }
                    case "Number": {
                        editor = GridNumberEditor;
                        break;
                    }
                    case "Boolean": {
                        editor = GridCheckBoxEditor;
                        break;
                    }
                    case "Enum": {
                        editor = GridComboBoxEditor;
                        let labels = _.get(fcdColumnDef, "Display.enum", fcdColumnDef.Enum);
                        for (let x = 0; x < fcdColumnDef.Enum.length; x++) {
                            let value = fcdColumnDef.Enum[x];
                            let label = fcdColumnDef.Enum[x];
                            if (!_.isEmpty(labels[x])) {
                                label = labels[x];
                            }
                            comboBoxOptions.push({label: label, value: value});
                        }
                        break;
                    }
                    case "Ref": {
                        editor = GridRefComboBoxEditor;
                        refColumn = true;
                        fcdColumnDef.instanceMap = {};
                        let refTypes = _.get(fcdColumnDef, "Ref.types", []);
                        // Go through the type and create an instance map and combo options for
                        // this ref field.
                        refTypes.forEach((type) => {
                            let instances = _.get(this.fcd, "refInstances." + type.typePath, []);
                            instances.forEach((instance) => {
                                let instanceRef = {
                                    "@ref": {
                                        instance: {
                                            construct: type.construct,
                                            instanceId: instance.id,
                                            instanceName: instance.instanceName,
                                            organizationId: instance.organizationId,
                                            subref: type.subref,
                                            type: type.typeName,
                                        },
                                        subref: type.subref,
                                    },
                                };

                                let option = {
                                    label: instance.instanceName,
                                    value: instance.id,
                                };

                                fcdColumnDef.instanceMap[instance.id] = instanceRef;
                                comboBoxOptions.push(option);
                            });
                        });
                        comboBoxOptions = _.sortBy(comboBoxOptions, ["label"]);
                        break;
                    }
                    case "Select": {
                        editor = GridSelectComboBoxEditor;
                        let props = _.get(fcdColumnDef, "Select.options.@propertyOrder", []);
                        props.forEach((prop) => {
                            let value = prop;
                            let propObject = fcdColumnDef.Select.options[prop];
                            let label = _.get(propObject, "Display.label", value);
                            comboBoxOptions.push({label: label, value: value});
                        });

                        break;
                    }
                    default: {
                        addColumn = false;
                        addHiddenDataCol = true;
                        // An object can display columns from the sub data so look up if this property
                        // has any deep value displays.
                        let subColumnArray = _.get(deepColumns, columnDefName);
                        if (subColumnArray) {
                            // Begin adding the sub columns
                            for (let subIdx = 0; subIdx < subColumnArray.length; subIdx++) {
                                let subColumn = subColumnArray[subIdx];

                                // Pull out the last property name
                                let propSegments = subColumn["@displayValue"].split(".");
                                let lastPropName = propSegments[propSegments.length - 1];

                                let valuePath = subColumn["@displayValue"];
                                let columnName = _.get(subColumn, "label", lastPropName);
                                let field = "@subData:" + columnDefName + ":" + subIdx;
                                let columnDef = {
                                    id: field,
                                    name: columnName,
                                    field: field,
                                    minWidth: 100,
                                    width: 150,
                                    maxWidth: 500,
                                    formatter: subColumnFormatter,
                                    launchDetailForm: true,
                                    valuePath: valuePath,
                                };
                                this.columns.push(columnDef);
                            }
                        }
                    }
                }
            }
            if (addColumn) {
                let columnDef = {
                    id: columnDefName,
                    name: columnHeading,
                    field: columnDefName,
                    minWidth: 100,
                    width: 150,
                    maxWidth: 500,
                    editor: allowEdit ? editor : null,
                    options: comboBoxOptions,
                    formatter: _.get(editor, "Formatter", formatter),
                    fcdColumnDef: fcdColumnDef,
                    refColumn: refColumn,
                    sortable: _.get(
                        this.fcd,
                        "configSettings.Display.table.columns." + columnDefName + ".showSort",
                        false
                    ),
                    launchDetailForm: !allowEdit,
                };
                this.columns.push(columnDef);
            }
        });

        ValueBuilder.getJTypeInstanceData(colConfigSettings, this._defaultRow);
        if (addHiddenDataCol && this.allowDetailForm) {
            let complexDataFormatter = function () {
                return '<div class="complex_cell" title="Complex Data">...</div>';
            };
            let columnDef = {
                id: "@complexData",
                name: "",
                field: "@complexData",
                minWidth: 50,
                width: 50,
                maxWidth: 80,
                formatter: complexDataFormatter,
                launchDetailForm: true,
            };
            this.columns.push(columnDef);
        }

        let forceFitColumns = false;
        if (_.has(this.fcd, "configSettings.Display.table.forceFitColumns")) {
            forceFitColumns = _.get(this.fcd, "configSettings.Display.table.forceFitColumns");
        } else {
            forceFitColumns = this.columns.length < 6;
        }
        let enableTextSelectionOnCells = _.get(
            this.fcd,
            "configSettings.Display.table.enableTextSelectionOnCells",
            true
        );

        let options = {
            asyncEditorLoading: true,
            enableCellNavigation: true,
            enableColumnReorder: false,
            multiColumnSort: true,
            autoResize: true,
            enableAsyncPostRender: true,
            syncColumnCellResize: true,
            selectionType: "row_multi_select",
            fullWidthRows: true,
            forceFitColumns: forceFitColumns,
            editable: true,
            autoEdit: true,
            enableTextSelectionOnCells: enableTextSelectionOnCells,
        };

        this.gridContainer = this.containingElem.find(".grid_container");
        this.grid = new Slick.Grid(this.gridContainer, this.dataView, this.columns, options);
        this.grid.setSelectionModel(new GridSelectionModel());
    }

    _setupDetailForm(scope) {
        this.detailFormApi = scope.DetailFormApi;
    }

    /**
     * Render the detail form which is a form data of a single row.
     * @param instanceInfo
     * @private
     */
    _renderDetailForm(instanceInfo) {
        if (this.allowDetailForm) {
            this.detailFormApi.removeListener("ValueChanged", this._onDetailFormChange);
            this.detailFormApi.removeListener("MultiEditActive", this._onDetailFormMultiEditActive);
            this.detailFormApi.removeListener("InProgress", this._onDetailFormInProgress);
            this.detailFormApi.removeListener("AllControlsLoaded", this._onDetailFormLoaded);

            let cs = instanceInfo;
            let cd = instanceInfo.configData;

            let ri;

            if (instanceInfo.hasOwnProperty("configSettings")) {
                cs = instanceInfo.configSettings.jtype
                    ? instanceInfo.configSettings.jtype
                    : instanceInfo.configSettings;
                ri = instanceInfo.configSettings.instances;
            }
            // Add in a label for the form.
            cs.Display = {
                label: _.get(
                    this.fcd,
                    "configSettings.Display.table.detailFormLabel",
                    this.fcd.displayInfo.label + " Row Selection"
                ),
            };

            this.detailFormApi.once(
                "AllControlsLoaded",
                (this._onDetailFormLoaded = this._onDetailFormLoaded.bind(this))
            );

            this.scope.detailFormVisible = true;
            this.detailFormApi.render(cs, {
                configurationData: cd,
                refInstances: ri,
                refTypes: instanceInfo.refTypes,
                displayModeOptions: {
                    multiEditMode: this.scope._multiEditMode,
                },
            });
            this._fireInProgressStatusChange();
        }
    }

    // Show the details of the selected row.
    _showDetailForm() {
        this.scope._multiEditMode = false;
        let rows = this.grid.getSelectedRows();
        rows.sort();
        // If we are already displaying then just change the data.
        let instanceInfo = {
            configSettings: this.fcd.configSettings.value[0],
            configData: this._dataModel[rows[0]],
        };
        if (rows.length > 1) {
            instanceInfo.configData = {};
            instanceInfo.multiEdit = true;
            this.scope._multiEditMode = true;
            let rowData = [];
            for (let row of rows) {
                rowData.push(this._dataModel[row]);
            }
            let rowFCD = this.fcd.configSettings.value[0];
            let compareData = {
                jtype: rowFCD,
                rows: rowData,
                refInstances: this.fcd.refInstances,
            };

            let commonFCD = GlueJay.getCommonDataAndConfigSettings(compareData);
            instanceInfo.configData = commonFCD.commonConfigData;
            instanceInfo.configSettings = commonFCD.markedJType;
        } else {
            instanceInfo.configData = this._dataModel[rows[0]];
        }
        instanceInfo.configSettings.instances = this.fcd.refInstances;
        instanceInfo.refTypes = this.fcd.refTypes;
        this._renderDetailForm(instanceInfo);
    }

    _hideDetailForm() {
        this.scope._multiEditMode = false;
        this.scope.detailFormVisible = false;
        this._fireInProgressStatusChange();
        this.detailFormApi.removeListener("ValueChanged", this._onDetailFormChange);
        this.detailFormApi.removeListener("InProgress", this._onDetailFormInProgress);
        this.detailFormApi.removeListener("MultiEditActive", this._onDetailFormMultiEditActive);
    }

    _showDetailFormDropDown() {
        return this.scope.displayDetailDropDown && this.allowDetailForm;
    }

    _moveRows(up) {
        let rows = _.sortBy(this.grid.getSelectedRows());
        // Sanity check that we can move
        if (up) {
            if (rows[0] === 0) {
                return;
            }
        } else {
            if (rows[rows.length - 1] === this.dataView.getLength() - 1) {
                return;
            }
        }
        let origPosition = rows[0];
        let rowData = [];
        this.dataView.beginUpdate();
        rows.forEach((row) => {
            let movingItem = this.dataView.getItem(row);
            rowData.push(this.dataView.getItem(row));
            this.dataView.deleteItem(movingItem[idField]);
        });
        let newPosition = up ? origPosition - 1 : origPosition + 1;
        for (let x = rowData.length - 1; x >= 0; x--) {
            this.dataView.insertItem(newPosition, rowData[x]);
        }
        this.dataView.endUpdate();
        this._resetRowNumbers();

        let selectedRows = [];
        for (let x = 0; x < rowData.length; x++) {
            selectedRows.push(newPosition);
            newPosition++;
        }
        this.grid.setSelectedRows(selectedRows);
        if (up) {
            this.grid.scrollRowIntoView(selectedRows[0]);
        } else {
            this.grid.scrollRowIntoView(selectedRows[selectedRows.length - 1]);
        }
        this._cancelSort();
    }

    _checkMovePressed(up) {
        this.repeatMoves++;
        if (this.movePressed && this.repeatMoves < 500) {
            setTimeout(() => {
                this.rapidMove = true;
                this._moveRows(up);
                this._checkMovePressed(up);
            }, 50);
        }
    }

    /**
     * Cancel the sorting because the table was updated in some way.
     * @private
     */
    _cancelSort() {
        this.grid.setSortColumns([]);
        this.sortedColumns = [];
    }

    _dismissEditor() {
        // This is a cheap way to dismiss any open editors on a scroll.
        let activeRows = this.grid.getSelectedRows();
        if (this.grid.getEditorLock().isActive() && !this.keepEditorOpen) {
            this.grid.setActiveCell(activeRows[0], 0);
        }
    }

    _resetRowNumbers() {
        this.grid.invalidate();
    }

    _launchRef(args) {
        let refInstances = [];
        let item = this.dataView.getItem(args.row);
        let fieldName = this.columns[args.cell].field;
        let refValue = item[fieldName];
        if (!_.isEmpty(refValue)) {
            if (_.has(refValue, "@ref.instance")) {
                refInstances.push(_.get(refValue, "@ref.instance"));
            } else {
                refInstances = _.get(refValue, "@ref.instances", []);
            }
        }
        let colConfigSettings = this.fcdColumnDefs[args.cell];
        let types = [];
        let construct = "";
        colConfigSettings.Ref.types.forEach((type) => {
            types.push(type.displayName);
            // The construct for all the types should always be the same.
            construct = type.construct;
        });
        let fcd = {
            configSettings: colConfigSettings,
            instances: this.fcd.instances,
        };
        launchRef(this.scope, construct, types, refInstances, fcd);
    }

    _resizeGrid() {
        // MaxRowCount should add column header and horizontal scroll bar.
        let minRowCount = _.get(
            this.fcd,
            "configSettings.Display.table.minLineCount",
            defaultMinLineCount
        );

        // We add one row for the column headers.
        let rowCountSize = Math.max(minRowCount + 1, minimumRowSize);
        this.gridContainer[0].style.height = rowCountSize * this.rowHeight + "px";

        setTimeout(() => {
            this.grid.resizeCanvas();
        }, 100);
    }

    _sortOnColumns() {
        if (!_.isEmpty(this.sortedColumns)) {
            let rows = this.grid.getSelectedRows();
            let selectedValues = [];
            for (let i = 0; i < rows.length; i++) {
                selectedValues.push(this.dataView.getItemByIdx(rows[i]));
            }
            this.dataView.getItemByIdx();
            this.dataView.beginUpdate();
            this.dataView.sort((r1, r2) => {
                // Sort by each column. If the value is the same for the column then proceed
                // to the next sorting column.
                for (let colIdx = 0; colIdx < this.sortedColumns.length; colIdx++) {
                    let field = this.sortedColumns[colIdx].sortCol.field;
                    let order = this.sortedColumns[colIdx].sortAsc ? 1 : -1;
                    let value1 = this._getSortValue(this.sortedColumns[colIdx].sortCol, r1[field]);
                    let value2 = this._getSortValue(this.sortedColumns[colIdx].sortCol, r2[field]);
                    let result = (value1 === value2 ? 0 : value1 > value2 ? 1 : -1) * order;
                    // If the values are different for this column, then sorting on this value takes precedence
                    if (result !== 0) {
                        return result;
                    }
                }
                return 0;
            });
            this.dataView.endUpdate();
            this.grid.invalidate();
            this.grid.render();

            rows = selectedValues.map((value) => {
                return this.dataView.getRowById(value[idField]);
            });
            this.grid.setActiveCell(rows[0], 0);
            this.grid.setSelectedRows(rows);

            this.grid.scrollRowIntoView(rows[0]);
        }
    }

    _getSortValue(col, value) {
        let sortValue = value;
        if (col.refColumn) {
            sortValue = _.get(value, "@ref.instance.instanceName", "");
        } else if (col.fcdColumnDef.fieldType === "Enum") {
            sortValue = "";
            col.options.forEach((option) => {
                if (_.isEqual(value, option.value)) {
                    sortValue = option.label;
                }
            });
        }

        return sortValue;
    }

    // ************************************************
    // Emitters
    // ************************************************

    _fireValueChanged(rows, silent) {
        let gridVal = [];
        if (this.dataView) {
            gridVal = this.dataView.getItems();
        }
        // If the change was limited, the change only those rows
        if (!_.isEmpty(rows) && rows.length < 20) {
            rows.forEach((rowIdx) => {
                let gridData = this.dataView.getItem(rowIdx);
                let modelData = _.cloneDeep(gridData);
                delete modelData[idField];
                delete modelData[rowNumField];
                this._dataModel[rowIdx] = modelData;
            });
        } else {
            this._dataModel = _.cloneDeep(gridVal);
            // Prune the model of grid fields
            this._dataModel.forEach((row) => {
                delete row[idField];
                delete row[rowNumField];
            });
        }
        //Get the new Value
        let newVal = this.getValue();
        if (!silent) {
            this.emit("ValueChanged", {
                name: this.fcd.name,
                containingElem: this.containingElem,
                controlAdapter: this,
                formControlDefinition: this.fcd,
                oldValue: this._oldValue,
                newValue: newVal,
                forceChange: true,
            });
        }
        this._oldValue = newVal;
    }

    _fireComplete() {
        this.emit("InitializationComplete", {
            initializedValue: this.getValue(),
        });
    }

    _fireInProgressStatusChange() {
        let newInProgressStatus = this.isInProgress();
        if (newInProgressStatus !== this._oldInProgressStatus) {
            this.emit("InProgress", {
                name: this.fcd.name,
                containingElem: this.containingElem,
                controlAdapter: this,
                formControlDefinition: this.fcd,
                oldInProgressStatus: this._oldInProgressStatus,
                newInProgressStatus: newInProgressStatus,
            });
            this._oldInProgressStatus = newInProgressStatus;
        }
    }

    // ************************************************
    // Listener Functions
    // ************************************************

    _onRowCountChange(/*event, args*/) {
        this.grid.updateRowCount();
        this.grid.render();
        this._fireValueChanged();
    }

    _onRowChange(event, args) {
        this.grid.invalidateRows(args.rows);
        this.grid.render();
        // If a rapid move is going on, wait for the update.
        if (!this.rapidMove) {
            this._fireValueChanged(args.rows);
        }
        // Redisplay the detail form, but only if the data was not updated from the form originally
        if (this.scope.detailFormVisible && !this._changeFromDetailForm) {
            this._showDetailForm();
        }
        this._changeFromDetailForm = false;
    }

    _onGridResize() {
        this._dismissEditor();
        this.grid.resizeCanvas();
    }

    _onCellChange(e, args) {
        this._cancelSort();
        this.dataView.updateItem(args.item[idField], args.item);
        this._sortOnColumns();
    }

    _onDetailFormChange(e) {
        let row = this.grid.getSelectedRows();
        let currentModel = this.dataView.getItem(row);
        // Clean up the old fields because we are going to get the new values from the form
        Object.keys(currentModel).forEach((field) => {
            // Ingnore the grid fields
            if (!field.startsWith("@")) {
                delete currentModel[field];
            }
        });
        // Add in the grid id and the row column into the data.
        let newVal = Object.assign({}, currentModel, e.newValue["@VALUE"]);
        this._changeFromDetailForm = true;
        this.dataView.updateItem(newVal[idField], newVal);
    }

    _onDetailFormMultiEditActive(e) {
        console.log("Detail form multi-edit active = " + e.newValue);
    }

    _onDetailFormInProgress() {
        this._fireInProgressStatusChange();
    }

    _onMultiApply() {
        let currentFormValue = this.detailFormApi.getValue();
        let nonSelectedPaths = this.detailFormApi.getNonSelectedPaths();
        if (_.has(currentFormValue, "@VALUE")) {
            let selectedItems = [];
            let selectedIndexes = _.sortBy(this.grid.getSelectedRows());
            this.dataView.beginUpdate();
            selectedIndexes.forEach((idx) => {
                selectedItems.push(this.dataView.getItem(idx));
            });

            selectedItems.forEach((item) => {
                _.mergeWith(item, currentFormValue["@VALUE"], function (objValue, srcValue) {
                    if (_.isArray(objValue) || _.isArray(srcValue)) {
                        return srcValue;
                    }
                });
                // Loop through the non selected fields and delete
                nonSelectedPaths.forEach((path) => {
                    // Prune of the front
                    let targetPath = path;
                    if (path.startsWith("@VALUE.")) {
                        targetPath = path.substring("@VALUE.".length);
                    }
                    if (_.has(item, targetPath)) {
                        let pathSegments = targetPath.split(".");
                        let targetProp = pathSegments.pop();
                        let targetObject = item;
                        if (!_.isEmpty(pathSegments)) {
                            targetPath = pathSegments.join(".");
                            targetObject = _.get(item, targetPath);
                        }
                        delete targetObject[targetProp];
                    }
                });
                this.dataView.updateItem(item[idField], item);
            });
            this.dataView.endUpdate();
        }
    }

    _onScroll(/*event, args*/) {
        // The grid will sometimes scroll a small bit when the editor is being opened due to the
        // fact that the editor components are coming into focus
        if (!this.grid.openingEditor) {
            this._dismissEditor();
        }
    }

    _onSelectedRowsChanged(event, args) {
        if (args.rows.length === 1) {
            this.scope.displayDetailDropDown = true;
            if (this.scope.detailFormVisible) {
                this._showDetailForm();
            }
        } else if (args.rows.length > 1 && this._multiEdit) {
            this.scope.displayDetailDropDown = true;
            if (this.scope.detailFormVisible) {
                this._showDetailForm();
            }
        } else {
            this.scope.displayDetailDropDown = false;
            this._hideDetailForm();
        }
        // setTimeout(() => {
        //     this.scope.$apply();
        // });

        let continous = true;
        if (!_.isEmpty(args.rows)) {
            let sortedRows = _.sortBy(args.rows);
            if (sortedRows.length > 1) {
                for (let x = 1; x < sortedRows.length; x++) {
                    if (sortedRows[x - 1] !== sortedRows[x] - 1) {
                        continous = false;
                    }
                }
            }
        } else {
            continous = false;
        }
        if (continous) {
            this.scope.disableMove = false;
        } else {
            this.scope.disableMove = true;
        }
        setTimeout(() => {
            this.scope.$apply();
        });
    }

    _onClick(e, args) {
        // This is a horrible way to test for the icon press, but we need to plug into the
        // slickgrid click.
        if (this.columns[args.cell].refColumn) {
            let path = e.originalEvent.composedPath();
            if (!_.isEmpty(path) && _.isArray(path)) {
                for (let x = 0; x < path.length; x++) {
                    if (path[x].tagName === "LAUNCH-DEFAULT-ICON-GL") {
                        return this._launchRef(args);
                    }
                }
            }
        }
    }

    _onBeforeEditCell(/*e, args*/) {
        if (this._readOnlyOn) {
            return false;
        }
    }

    _onDblClick(e /*, args*/) {
        let cell = this.grid.getCellFromEvent(e);
        if (this.columns[cell.cell].launchDetailForm) {
            this._showDetailForm();
        }
    }

    _onSort(e, args) {
        this.grid.getEditorLock().cancelCurrentEdit();
        this.sortedColumns = args.sortCols;
        this._sortOnColumns();
    }

    _onDocClick(/*e, args*/) {
        if (this.grid.getEditorLock().isActive() && !this.keepEditorOpen) {
            this.grid.getEditController().commitCurrentEdit();
        }
    }

    _onDetailFormLoaded() {
        let detailFormElem = this.containingElem[0].querySelector(
            ".grid_row_detail_container gl-form-renderer"
        );
        if (this.scope._multiEditMode) {
            detailFormElem.appendChild(this._multiEditApply);
            this.detailFormApi.addListener(
                "MultiEditActive",
                (this._onDetailFormMultiEditActive = this._onDetailFormMultiEditActive.bind(this))
            );
        } else {
            if (detailFormElem.querySelector(".multi_edit_apply")) {
                detailFormElem.removeChild(this._multiEditApply);
            }
            this.detailFormApi.addListener(
                "ValueChanged",
                (this._onDetailFormChange = this._onDetailFormChange.bind(this))
            );
            this.detailFormApi.addListener(
                "InProgress",
                (this._onDetailFormInProgress = this._onDetailFormInProgress.bind(this))
            );
        }
    }

    _handleIntersect(entries, observer) {
        entries.forEach((entry) => {
            if (entry.intersectionRatio !== this._prevRatio) {
                if (this.grid.getDataLength() > 0 && entry.intersectionRatio >= 0.1) {
                    this.grid.setData(this.grid.getData());
                    this.grid.setColumns(this.grid.getColumns());
                }
                this._prevRatio = entry.intersectionRatio;
            }
        });
    }
}

module.exports = ArrayGridAdapter;
